/**
 * Floating Liquid Glass Header
 *
 * Handles:
 *  - the hamburger toggle, which expands/collapses the inline menu panel
 *    (built in Liquid from section.settings.main_menu);
 *  - a page scrim + scroll lock while the menu is open;
 *  - gradual scroll-based fade/hide of the whole header.
 *
 * Header/menu audit 2026-09-23:
 *  - scrolling with the menu open faded the header to 'hidden' (opacity 0,
 *    pointer-events none) while the panel stayed "open" - an invisible,
 *    unclickable menu. The fade is now suspended while the menu is open, and
 *    the page behind is locked on phones.
 *  - Escape / outside click now return focus to the toggle when focus was
 *    inside the panel; tabbing out of the header closes the menu.
 *  - The closed panel is `inert`, so it can never be focused or read.
 *  - Dead code for an inline search bar that no longer exists was removed.
 *  - The cart link no longer sets a stale aria-expanded it never reset.
 */
(function () {
  const MOBILE = window.matchMedia('(max-width: 749px)');

  class FloatingGlassHeader extends HTMLElement {
    connectedCallback() {
      this.menuToggle = this.querySelector('.fgh-menu-toggle');
      this.menuPanel = this.querySelector('.fgh-menu-panel');
      if (this.menuPanel) this.menuPanel.inert = true;
      this.menuToggle?.addEventListener('click', this.onMenuToggleClick);
      // Start the card images downloading on finger-down, ~100 ms before the
      // click fires; the panel stays invisible and inert until it opens.
      this.menuToggle?.addEventListener('pointerdown', this.onMenuTogglePress, { passive: true });

      this.cartLink = this.querySelector('.fgh-cart-link');
      this.cartLink?.removeAttribute('aria-expanded');
      this.cartLink?.addEventListener('click', this.onCartClick);

      this.scrim = document.createElement('div');
      this.scrim.className = 'fgh-scrim';
      this.scrim.setAttribute('aria-hidden', 'true');
      this.after(this.scrim);

      document.addEventListener('keydown', this.onKeyDown);
      document.addEventListener('click', this.onDocumentClick);
      this.addEventListener('focusout', this.onFocusOut);

      this.setupScrollFade();
    }

    disconnectedCallback() {
      this.menuToggle?.removeEventListener('click', this.onMenuToggleClick);
      this.menuToggle?.removeEventListener('pointerdown', this.onMenuTogglePress);
      this.cartLink?.removeEventListener('click', this.onCartClick);
      document.removeEventListener('keydown', this.onKeyDown);
      document.removeEventListener('click', this.onDocumentClick);
      this.removeEventListener('focusout', this.onFocusOut);
      this.scrim?.remove();
      document.documentElement.classList.remove('fgh-menu-lock');
      if (this.scrollHandler) window.removeEventListener('scroll', this.scrollHandler);
    }

    /* ------------------------------------------------------------------
     * Hamburger -> inline expanding menu panel
     * ------------------------------------------------------------------ */
    onMenuToggleClick = (event) => {
      event.preventDefault();
      this.isMenuOpen() ? this.closeMenu() : this.openMenu();
    };

    onMenuTogglePress = () => {
      if (this.menuPanel?.hidden && !this.isMenuOpen()) {
        clearTimeout(this.hideTimer);
        this.menuPanel.hidden = false;
      }
    };

    isMenuOpen() {
      return this.menuToggle?.getAttribute('aria-expanded') === 'true';
    }

    openMenu() {
      if (!this.menuPanel || !this.menuToggle) return;
      clearTimeout(this.hideTimer);
      this.menuPanel.hidden = false;
      this.menuPanel.inert = false;
      this.showHeader?.();
      requestAnimationFrame(() => {
        this.menuPanel.setAttribute('data-open', '');
        this.scrim.setAttribute('data-open', '');
      });
      this.menuToggle.setAttribute('aria-expanded', 'true');
      if (MOBILE.matches) document.documentElement.classList.add('fgh-menu-lock');
    }

    closeMenu({ restoreFocus = false } = {}) {
      if (!this.menuPanel || !this.menuToggle) return;
      const focusWasInside = this.menuPanel.contains(document.activeElement);
      this.menuPanel.removeAttribute('data-open');
      this.scrim.removeAttribute('data-open');
      this.menuToggle.setAttribute('aria-expanded', 'false');
      this.menuPanel.inert = true;
      document.documentElement.classList.remove('fgh-menu-lock');
      if (restoreFocus || focusWasInside) this.menuToggle.focus({ preventScroll: true });

      // transitionend is not guaranteed (reduced motion, tab hidden), so a
      // timer backs it up.
      clearTimeout(this.hideTimer);
      this.hideTimer = setTimeout(() => {
        if (!this.isMenuOpen()) this.menuPanel.hidden = true;
      }, 400);
    }

    onKeyDown = (event) => {
      if (event.key === 'Escape' && this.isMenuOpen()) {
        this.closeMenu({ restoreFocus: this.contains(document.activeElement) });
      }
    };

    onDocumentClick = (event) => {
      if (this.isMenuOpen() && !this.contains(event.target)) this.closeMenu();
    };

    onFocusOut = (event) => {
      // Keyboard users tabbing past the last menu link leave the header.
      if (this.isMenuOpen() && event.relatedTarget && !this.contains(event.relatedTarget)) {
        this.closeMenu();
      }
    };

    /* ------------------------------------------------------------------
     * Cart
     * ------------------------------------------------------------------ */
    onCartClick = (event) => {
      const cartDrawer = document.getElementById('cart-drawer');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        event.preventDefault();
        if (this.isMenuOpen()) this.closeMenu();
        cartDrawer.open();
      }
      // No drawer on this page -> the link navigates to /cart normally.
    };

    /* ------------------------------------------------------------------
     * Scroll-based gradual fade
     *
     * Anchored to the point where scroll direction last changed (not the
     * document top), carrying opacity across the flip. applyOpacity owns the
     * state attribute, so <= HIDDEN_AT is always 'hidden' - the only state
     * that disables pointer-events. Suspended entirely while the menu is open.
     * ------------------------------------------------------------------ */
    setupScrollFade() {
      if (!this.classList.contains('fgh-header--scroll-fade')) return;

      const HIDDEN_AT = 0.02;
      const VISIBLE_AT = 0.98;
      const TOP_ZONE = 4;
      const fadeDistance = Math.max(1, Number(this.dataset.fghScrollFadeDistance) || 160);

      let lastScrollY = window.scrollY || 0;
      let anchorY = lastScrollY;
      let anchorOpacity = 1;
      let currentOpacity = 1;
      let direction = 0;
      let ticking = false;

      const applyOpacity = (value) => {
        const opacity = Math.min(1, Math.max(0, value));
        if (opacity === currentOpacity && this.dataset.fghScrollState) return;
        currentOpacity = opacity;
        this.dataset.fghScrollState =
          opacity <= HIDDEN_AT ? 'hidden' : opacity >= VISIBLE_AT ? 'visible' : 'fading';
        this.style.setProperty('--fgh-scroll-opacity', String(opacity));
      };

      this.showHeader = () => {
        direction = 0;
        anchorY = lastScrollY = window.scrollY || 0;
        anchorOpacity = 1;
        applyOpacity(1);
      };

      applyOpacity(1);

      this.scrollHandler = () => {
        if (ticking) return;
        ticking = true;

        requestAnimationFrame(() => {
          ticking = false;
          const scrollY = window.scrollY || 0;

          if (this.isMenuOpen()) {
            lastScrollY = anchorY = scrollY;
            return;
          }

          const delta = scrollY - lastScrollY;
          if (delta !== 0) {
            const nextDirection = delta > 0 ? 1 : -1;
            if (nextDirection !== direction) {
              direction = nextDirection;
              anchorY = lastScrollY;
              anchorOpacity = currentOpacity;
            }
          }
          lastScrollY = scrollY;

          if (scrollY <= TOP_ZONE) {
            this.showHeader();
          } else if (direction > 0) {
            applyOpacity(anchorOpacity - (scrollY - anchorY) / fadeDistance);
          } else if (direction < 0) {
            applyOpacity(anchorOpacity + (anchorY - scrollY) / fadeDistance);
          }
        });
      };

      window.addEventListener('scroll', this.scrollHandler, { passive: true });
    }
  }

  if (!customElements.get('fgh-header')) {
    customElements.define('fgh-header', FloatingGlassHeader);
  }
})();